import { useRef, useCallback } from 'react';
import YouboraTracking from 'providers/player/YouboraTracking';
import { PlayingAsset } from 'types/Player';
import { isCatchupPlayerAsset, isLivePlayerAsset, isRecordingPlayerAsset, isVodPlayerAsset } from 'utils/fnTypeGuards';
import { epgMode } from 'types/EpgTypes';
import { ContentLinkType } from 'types/Tracking';

export interface ContentTrackingParams {
    section: string;
    sectionOrder: number;
    column: number;
    row: number;
    contentId: string;
    contentLink?: string;
    contentLinkType?: ContentLinkType;
    channelId?: string;
    epgId?: string;
    epgMode?: epgMode;
}

export interface ContentPlayParams {
    contentId: string;
    contentLink?: string;
    contentLinkType?: ContentLinkType;
}
export interface UseYouboraContentTrackingReturn {
    handleContentFocus: (params: ContentTrackingParams) => void;
    handleContentFocusOut: () => void;
    handleContentClick: (params: ContentTrackingParams) => void;
    trackContentPlay: (contentId: string, customParams?: { contentLink?: string; contentLinkType?: ContentLinkType }) => void;
    trackPlayerInteraction: (interactionName: string, assetId?: string) => void;
    trackContentPlayFromAsset: (asset: PlayingAsset) => void;
}

export const useYouboraContentTracking = (): UseYouboraContentTrackingReturn => {
    const currentContentRef = useRef<ContentTrackingParams | null>(null);

    const handleContentFocus = useCallback((params: ContentTrackingParams) => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized) {
            return;
        }

        currentContentRef.current = params;

        try {
            youboraTracking.trackContentFocusIn(params.section, params.sectionOrder, params.column, params.row, params.contentId);
        } catch (error) {
            console.error('[Youbora] Error tracking content focus in:', error);
        }
    }, []);

    const handleContentFocusOut = useCallback(() => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized || !currentContentRef.current) {
            return;
        }

        try {
            youboraTracking.trackContentFocusOut();
        } catch (error) {
            console.error('[Youbora] Error tracking content focus out:', error);
        }

        currentContentRef.current = null;
    }, []);

    const handleContentClick = useCallback((params: ContentTrackingParams) => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized) {
            return;
        }

        try {
            let customParams: any;

            if (params.contentLink && params.contentLinkType) {
                customParams = { contentLink: params.contentLink, contentLinkType: params.contentLinkType };
            }

            if (params.channelId || params.epgId || params.epgMode) {
                customParams = {
                    ...customParams,
                    ...(params.channelId && { channelId: params.channelId }),
                    ...(params.epgId && { epgId: params.epgId }),
                    ...(params.epgMode && { epgMode: params.epgMode }),
                };
            }

            youboraTracking.trackContentClick(
                params.section,
                params.sectionOrder,
                params.column,
                params.row,
                params.contentId,
                customParams
            );
        } catch (error) {
            console.error('[Youbora] Error tracking content click:', error);
        }
    }, []);

    const trackContentPlay = useCallback(
        (contentId: string, customParams?: { contentLink?: string; contentLinkType?: ContentLinkType }) => {
            if (!contentId || contentId.trim() === '') {
                return;
            }

            const youboraTracking = YouboraTracking.getInstance();

            if (!youboraTracking.isInitialized) {
                return;
            }

            try {
                youboraTracking.trackContentPlay(contentId, customParams);
            } catch (error) {
                console.error('[Youbora] Error tracking content play:', error);
            }
        },
        []
    );

    const trackPlayerInteraction = useCallback((interactionName: string, contentId?: string) => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized) {
            return;
        }

        if (!contentId || contentId.trim() === '') {
            return;
        }

        try {
            youboraTracking.trackPlayerInteraction(interactionName, contentId);
        } catch (error) {
            console.error('[Youbora] Error tracking player interaction:', error);
        }
    }, []);

    const trackContentPlayFromAsset = useCallback(
        (asset: PlayingAsset) => {
            if (!asset?.id || asset.id.trim() === '') {
                return;
            }

            // Handle VOD content (Movies and Series Episodes)
            if (isVodPlayerAsset(asset)) {
                if (asset.isMovie) {
                    trackContentPlay(asset.id, {
                        contentLink: asset.id,
                        contentLinkType: 'contentId',
                    });

                    const { movieGroupId } = asset as any;
                    if (movieGroupId) {
                        trackContentPlay('null', {
                            contentLink: movieGroupId,
                            contentLinkType: 'movieGroupId',
                        });
                    }
                } else if (asset.seriesId) {
                    trackContentPlay(asset.id, {
                        contentLink: asset.id,
                        contentLinkType: 'contentId',
                    });

                    const { seriesSeasonId, seasonId, seasonNumber } = asset as any;
                    // Construct seriesSeasonId from available data if not provided
                    // Priority: seriesSeasonId > seasonId > constructed from seriesId + seasonNumber
                    let effectiveSeasonId = seriesSeasonId || seasonId;

                    if (!effectiveSeasonId && asset.seriesId && seasonNumber != null) {
                        effectiveSeasonId = `${asset.seriesId}_S${seasonNumber}`;
                    }

                    // Always call trackPlay for seriesSeasonId
                    if (effectiveSeasonId) {
                        trackContentPlay('null', {
                            contentLink: effectiveSeasonId,
                            contentLinkType: 'seriesSeasonId',
                        });
                    }

                    trackContentPlay('null', {
                        contentLink: asset.seriesId,
                        contentLinkType: 'seriesId',
                    });
                }
                return;
            }

            if (isLivePlayerAsset(asset) || isCatchupPlayerAsset(asset) || isRecordingPlayerAsset(asset)) {
                const { channelId, contentId, worldId, seriesId, isMovie, movieGroupId, seasonId } = asset as any;
                const epgId = asset.id;

                if (channelId) {
                    trackContentPlay('null', {
                        contentLink: channelId,
                        contentLinkType: 'channelId',
                    });
                }

                trackContentPlay('null', {
                    contentLink: epgId,
                    contentLinkType: 'epgId',
                });

                const effectiveContentId = contentId || worldId;

                if (isMovie && effectiveContentId) {
                    trackContentPlay(effectiveContentId, {
                        contentLink: effectiveContentId,
                        contentLinkType: 'contentId',
                    });

                    if (movieGroupId) {
                        trackContentPlay('null', {
                            contentLink: movieGroupId,
                            contentLinkType: 'movieGroupId',
                        });
                    }
                } else if (seriesId && effectiveContentId) {
                    trackContentPlay(effectiveContentId, {
                        contentLink: effectiveContentId,
                        contentLinkType: 'contentId',
                    });

                    // Construct seriesSeasonId from available data if not provided
                    const { seriesSeasonId, season_number } = asset as any;
                    let effectiveSeasonId = seriesSeasonId || seasonId;

                    if (!effectiveSeasonId && seriesId && season_number != null) {
                        effectiveSeasonId = `${seriesId}_S${season_number}`;
                    }

                    if (effectiveSeasonId) {
                        trackContentPlay('null', {
                            contentLink: effectiveSeasonId,
                            contentLinkType: 'seriesSeasonId',
                        });
                    }

                    trackContentPlay('null', {
                        contentLink: seriesId,
                        contentLinkType: 'seriesId',
                    });
                }
                return;
            }

            // Fallback: just track the asset ID without custom params
            trackContentPlay(asset.id, {
                contentLink: asset.id,
                contentLinkType: 'contentId',
            });
        },
        [trackContentPlay]
    );

    return {
        handleContentFocus,
        handleContentFocusOut,
        handleContentClick,
        trackContentPlay,
        trackPlayerInteraction,
        trackContentPlayFromAsset,
    };
};

export default useYouboraContentTracking;
